import { useGoogleOneTapLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import GoogleSignInButton from "./GoogleSignInButton";
import useAuthStore from "../store/authStore";

/**
 * Sprint-89 — Landing-page hero Google sign-in.
 *
 * Renders TWO things side-by-side for unauthenticated visitors:
 *   1. A passive Google One Tap prompt (top-right of the viewport, auto-shown
 *      by the Google Identity Services SDK as soon as the page mounts).
 *   2. A visible "Continue with Google" button under the hero CTAs as a
 *      fallback if One Tap is blocked or dismissed.
 *
 * Hides itself entirely if a user session already exists.
 */
export default function HeroGoogleSignIn() {
  const { user, loginWithGoogle } = useAuthStore();
  const navigate = useNavigate();

  // One-Tap hook: registers a passive popover prompt. Must be unconditionally
  // called BEFORE any early return so the hooks rule isn't violated. The hook
  // internally no-ops once a user is already authenticated via Google.
  useGoogleOneTapLogin({
    onSuccess: async (response) => {
      if (user) return; // session created in another tab, ignore
      try {
        const u = await loginWithGoogle(response.credential);
        toast.success(`Welcome, ${u.name?.split(" ")[0] || "back"}!`);
        navigate("/dashboard");
      } catch (err) {
        // Silent — One Tap is opportunistic; don't nag.
        // eslint-disable-next-line no-console
        console.debug("[one-tap] login failed:", err?.message);
      }
    },
    onError: () => {
      /* Silent — One Tap dismissal is fine. */
    },
    disabled: !!user,
  });

  if (user) return null;

  return (
    <div className="mt-6 max-w-sm" data-testid="hero-google-signin">
      <p className="text-xs font-medium text-slate-500 mb-2 tracking-wide uppercase">
        Sign in to start booking
      </p>
      <GoogleSignInButton redirectTo="/dashboard" />
    </div>
  );
}
